/**
 * 38. Sales Orders — list; detail; fulfillment status; delivery/invoice
 * actions; change/cancel; credit-limit blocking and request credit
 * approval.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  BlockedActionExplanation,
} from "@/components/ui";
import { useTranslation } from "@/lib/i18n";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { formatMoney, formatDate } from "@/lib/money";

interface SalesOrderRow {
  id: string;
  orderNumber: string;
  orderDate: string;
  customerName: string;
  status:
    | "Draft"
    | "Confirmed"
    | "Released"
    | "Fulfilled"
    | "Delivered"
    | "Invoiced"
    | "Completed"
    | "Cancelled";
  totalIncludingTax: string;
  currencyCode: string;
  creditBlocked: boolean;
}

export function SalesOrdersPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<SalesOrderRow>(
    ["sales", "orders"],
    "/sales/orders",
  );

  const blocked = (data?.items ?? []).filter((r) => r.creditBlocked);

  return (
    <div>
      <PageHeader
        title="Sales Orders"
        subtitle="Order-to-cash: confirm, approve, release, fulfill, deliver, invoice."
        actions={
          <PendingButton
            variant="primary"
            reason="salesOrderEditor"
            slice="ED-W2b"
          >
            {t("documentActions.newSalesOrder")}
          </PendingButton>
        }
      />
      {blocked.length > 0 && (
        <BlockedActionExplanation
          title="Why are these orders blocked?"
          reasons={blocked.map((r) => ({
            code: "CREDIT_LIMIT_EXCEEDED",
            message: `Order ${r.orderNumber} exceeds the customer's credit limit.`,
            resolutionPath: "Open the order and request credit approval.",
          }))}
        />
      )}
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Orders (${data.total})`}>
          <DataTable<SalesOrderRow>
            rows={data.items}
            columns={[
              {
                key: "orderNumber",
                label: "Number",
                render: (r) => (
                  <span className="atlas-mono">{r.orderNumber}</span>
                ),
              },
              {
                key: "orderDate",
                label: "Date",
                render: (r) => formatDate(r.orderDate),
              },
              { key: "customerName", label: "Customer" },
              {
                key: "totalIncludingTax",
                label: "Total",
                numeric: true,
                render: (r) => formatMoney(r.totalIncludingTax, r.currencyCode),
              },
              {
                key: "status",
                label: "Status",
                render: (r) => <StatusBadge status={r.status} />,
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
